import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { ESCAPE } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { produce } from 'immer';
import { asyncScheduler } from 'rxjs';

import { TagItemInfo } from '@blaze-shared/api-contracts-domain';
import { moveItemInArrayImmutably } from '@blaze-shared/shared-feature';
import {
  ColorPickerComponent,
  InplaceEditComponent,
  TooltipDirective,
} from '@blaze-shared/shared-ui';

/** Temporary model used to create new tags */
export type TemporaryTagItemInfo = Omit<TagItemInfo, 'id'> & { id: string };

export const DEFAULT_TAG: TemporaryTagItemInfo = {
  id: '',
  value: 'TAG',
  foregroundColor: '#373737',
  backgroundColor: '#f5f5f5',
};

@Component({
  selector: 'blaze-column-tags-editor-section',
  imports: [
    ColorPickerComponent,
    CommonModule,
    DragDropModule,
    InplaceEditComponent,
    MatIconModule,
    TooltipDirective,
  ],
  templateUrl: './column-tags-editor-section.component.html',
  styleUrls: ['./column-tags-editor-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnTagsEditorSectionComponent {
  @Input() tags: TemporaryTagItemInfo[] = [];
  @Input() tagNamePrefix?: string;
  @Output() tagsChange = new EventEmitter<TemporaryTagItemInfo[]>();

  @ViewChildren('editTagValue', { read: InplaceEditComponent })
  editTagValueInputs!: QueryList<InplaceEditComponent>;

  isEditing = false;

  onAddTag(): void {
    const newTag = this.generateNewTag();
    this.tags = [...this.tags, newTag];
    this.emitChanges();

    const focusNewlyCreatedTag = (): void => {
      const input = this.editTagValueInputs.find((c) => c.value === newTag.value);
      if (input) {
        input.edit();
      }
    };
    asyncScheduler.schedule(focusNewlyCreatedTag);
  }

  onDropTag(event: CdkDragDrop<any>): void {
    this.tags = moveItemInArrayImmutably(this.tags, event.previousIndex, event.currentIndex);
    this.emitChanges();
  }

  onEditTag(index: number, input: InplaceEditComponent): void {
    this.isEditing = true;
    input.edit();
  }

  onTagValueEdited(value: string, index: number): void {
    this.isEditing = false;
    if (this.tags.some((tag) => tag.value === value)) {
      return;
    }

    this.tags = produce(this.tags, (draft) => {
      draft[index].value = value;
    });
    this.emitChanges();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  onTagValueEditCanceled(): void {
    this.isEditing = false;
  }

  onRemoveTag(event: Event, index: number): void {
    const updatedTags = [...this.tags];
    updatedTags.splice(index, 1);
    this.tags = updatedTags;
    this.emitChanges();
  }

  onTagColorChange(
    color: string,
    type: 'foregroundColor' | 'backgroundColor',
    index: number,
  ): void {
    if (this.tags[index][type] !== color) {
      this.tags = produce(this.tags, (draft) => {
        draft[index][type] = color;
      });
      this.emitChanges();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isEditing && event.keyCode === ESCAPE) {
      event.stopPropagation();
    }
  }

  trackByValueFn(index: number, tag: TemporaryTagItemInfo): string {
    return tag.id || tag.value;
  }

  private emitChanges(changes: TemporaryTagItemInfo[] = this.tags): void {
    this.tagsChange.emit(changes);
  }

  private generateNewTag(): TemporaryTagItemInfo {
    const generateName = (index: number): string => {
      const generated = `${this.tagNamePrefix ?? DEFAULT_TAG.value}_${index}`;

      return this.tags.some((t) => t.value === generated) ? generateName(index + 1) : generated;
    };

    return {
      ...DEFAULT_TAG,
      id: crypto.randomUUID(),
      value: generateName(this.tags.length + 1),
    };
  }
}
